import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { HeaderService } from 'core';
import {
  CustomerDetailService,
  CustomersAddressDetailsDialogComponent,
  DeliveryDestinationEditResult,
  DeliveryDestinationNote,
  DeliveryDestinations,
} from 'customers';
import { startWith, switchMap } from 'rxjs';
import { OrderBusinessType } from '../../interfaces/order-proposal.model';
import { OrderProposalService } from '../../services/order-proposal.service';

type DestinationGeneralityField = 'indirizzo' | 'cap' | 'contatto' | 'orari';

@Component({
  selector: 'ord-select-destination',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  // Le traduzioni delle card arrivano dallo scope customers: la route è statica e non lo carica da sola.
  providers: [provideTranslocoScope('orders'), provideTranslocoScope('customers')],
  templateUrl: './select-destination.html',
  styleUrl: './select-destination.scss',
})
export class SelectDestination implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly customerDetailService = inject(CustomerDetailService);
  private readonly proposalService = inject(OrderProposalService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  // Destini del codice cliente scelto per la tipologia, sede del committente compresa
  private readonly proposalId = this.route.snapshot.queryParamMap.get('id') ?? '';
  private readonly type = (this.route.snapshot.queryParamMap.get('type') ?? 'Oil') as OrderBusinessType;

  readonly generalitiesFields: { labelKey: string; key: DestinationGeneralityField }[] = [
    { labelKey: 'delivery.destinations.generalities.address', key: 'indirizzo' },
    { labelKey: 'delivery.destinations.generalities.postcode', key: 'cap' },
    { labelKey: 'delivery.destinations.generalities.contact', key: 'contatto' },
    { labelKey: 'delivery.destinations.generalities.hours', key: 'orari' },
  ];

  readonly notesFields: { labelKey: string; key: keyof DeliveryDestinationNote }[] = [
    { labelKey: 'delivery.destinations.notes.freeNote', key: 'notaLibera' },
    { labelKey: 'delivery.destinations.notes.days', key: 'giorni' },
    { labelKey: 'delivery.destinations.notes.tailLift', key: 'sponda' },
  ];

  readonly searchControl = new FormControl('', { nonNullable: true });

  private readonly destinations = signal<DeliveryDestinations[]>([]);
  private readonly searchText = signal('');
  readonly selectedId = signal<string | null>(null);

  readonly filteredDestinations = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    const sorted = [...this.destinations()].sort((a, b) => Number(b.principale) - Number(a.principale));
    if (!search) {
      return sorted;
    }

    return sorted.filter(destination =>
      [destination.riferimento, destination.citta, destination.provincia, destination.cap, destination.indirizzo]
        .some(value => (value ?? '').toLowerCase().includes(search)),
    );
  });

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.nuovaPropostaDOrdine');

    this.searchControl.valueChanges
      .pipe(startWith(this.searchControl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.searchText.set(value));

    this.proposalService.ensure(this.proposalId).subscribe(() => {
      this.selectedId.set(this.proposalService.section(this.type)?.destino?.id ?? null);
      this.loadDestinations();
    });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
  }

  getGeneralityValue(destination: DeliveryDestinations, key: DestinationGeneralityField): string {
    if (key === 'contatto' || key === 'orari') {
      return destination.nota[key] ?? '-';
    }

    return destination[key] || '-';
  }

  onSelectDestination(destination: DeliveryDestinations): void {
    this.selectedId.set(destination.id);
  }

  // Dettagli in sola lettura con la matita: la modifica va sull'anagrafica del cliente, senza cambiarne il destino principale
  openDetails(event: MouseEvent, destination: DeliveryDestinations): void {
    event.stopPropagation();

    this.dialog.open(CustomersAddressDetailsDialogComponent, { data: destination, viewContainerRef: this.viewContainerRef })
      .afterClosed()
      .subscribe((result?: DeliveryDestinationEditResult) => {
        if (result?.action !== 'edit') {
          return;
        }

        const changes = result.value;
        this.customerDetailService.updateDeliveryDestination(this.proposalService.proposal()!.idBusinessPartner!, destination.id, {
          Nota: changes.nota !== undefined ? changes.nota : destination.nota.notaLibera,
          Riferimento: changes.riferimento !== undefined ? changes.riferimento : destination.riferimento,
          AddBolla: destination.nota.inBolla,
          Principale: destination.principale,
          Orari: changes.orari !== undefined ? changes.orari : destination.nota.orari,
          Contatto: changes.contatto !== undefined ? changes.contatto : destination.nota.contatto,
          Giorni: changes.giorni !== undefined ? changes.giorni : destination.nota.giorni,
          Sponda: changes.sponda !== undefined ? changes.sponda : destination.nota.sponda,
        }).pipe(
          // Il destino della proposta mostra la nota aggiornata
          switchMap(() => this.proposalService.open({ idOrdine: this.proposalId })),
        ).subscribe(() => this.loadDestinations());
      });
  }

  confirm(): void {
    const selectedId = this.selectedId();
    if (!selectedId) {
      return;
    }

    this.proposalService.setSection(this.type, { idDestino: selectedId }).subscribe(() => this.navigateBack());
  }

  cancel(): void {
    this.navigateBack();
  }

  private loadDestinations(): void {
    this.proposalService.getSectionDestinations(this.type).subscribe(destinations => this.destinations.set(destinations));
  }

  private navigateBack(): void {
    void this.router.navigate(['/new-order-proposal/shipping-payment'], {
      queryParams: { id: this.proposalId },
      replaceUrl: true,
    });
  }
}
